import { ICalendarProps } from "@/api_framework/api_modals/Calendar";
import { IClinicListResponseData } from "@/api_framework/api_modals/Clinics";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useStaffCalendarFilterAPI } from "@/app/hooks/api_hooks/StaffCalendar/useStaffCalendarFilterAPI";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import { useOrgClinicProvidersAPI } from "@/app/hooks/api_hooks/org/useOrgClinicProvidersAPI";
import { useOrgServicesByFilterAPI } from "@/app/hooks/api_hooks/org/useOrgServicesByFilterAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { setLoading } from "@/app_redux/reducers/loading_reducer";
import {
  ICalendarState,
  setCalendarFilter,
  setCalendarProviders,
} from "@/app_redux/reducers/slices/auth/staff_calendar_slice";
import { format } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

export interface ICalendarFiltersFormFields {
  date: string;
  city: string;
  clinics: string;
  providersId: Array<string>;
  service: string;
}
export interface ICalendarFromProps {
  ROTADate: string;
  clinicIds?: Array<string>;
  serviceId?: string;
  cityId?: string;
}

const CalendarViewFilterComponent = ({ setSlideIndex }) => {
  /* Instantiations */
  const [_filtersError, setFiltersErrors] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const { getRouteKey } = useRouter();
  const stringConstants = useStringConstants();
  const { mappedCityId, mappedClinicId } = useAppSelector((state) => state.root.UIStates);
  const { serviceTypes, execute: fetchOrgServicesByFilter } = useOrgServicesByFilterAPI();
  const didFetchStaffCalendarServices = useRef(false);

  const [initialFetched, setInitialFetched] = useState<boolean>(false);

  /* API Hooks Starts Here */
  const fetchCities = useOrgCitiesListAPI("CALENDAR");

  /* Redux States Ends Here */
  const { routeState } = useRouteInfo(getRouteKey("STAFF_CALENDAR", "id"));
  const { clinics, providers, cities, calendarFilter } = routeState.state as ICalendarState;

  const serviceTypeGroups = useMemo(() => {
    return (serviceTypes ?? []).map((serviceType) => ({
      id: serviceType?.id,
      name: serviceType?.name,
      serviceIds: (serviceType?.services ?? []).map((service) => service?.service_id),
    }));
  }, [serviceTypes]);

  const orgServices = useMemo(() => {
    return (serviceTypes ?? []).flatMap((serviceType) =>
      (serviceType?.services ?? []).map((service) => ({
        ...service,
        service_type_id: serviceType?.id,
        service_type_name: serviceType?.name,
      })),
    );
  }, [serviceTypes]);

  const mapSelectedTypeIdToServiceIds = (selectedTypeId: string) => {
    if (!selectedTypeId || selectedTypeId === "-1") {
      return (orgServices ?? []).map((s) => s?.service_id);
    }

    const selectedType = serviceTypeGroups.find((group) => group.id === selectedTypeId);
    if (selectedType?.serviceIds?.length > 0) {
      return selectedType.serviceIds;
    }

    return (orgServices ?? [])
      .filter((service: any) => service?.service_id === selectedTypeId)
      .map((service: any) => service?.service_id);
  };

  const fetchProviders = useOrgClinicProvidersAPI("CALENDAR");
  const fetchBookingDetails = useStaffCalendarFilterAPI();

  /* Actions and Handlers */
  const validateConditionalFormFields = (data: ICalendarFiltersFormFields) => {
    let isValid = false;
    if (data?.city !== "" && data?.clinics !== "" && data?.providersId?.length > 0) {
      isValid = true;
      setFiltersErrors(false);
    }
    return isValid;
  };

  const onSubmit = (data: ICalendarFiltersFormFields) => {

    const isFormSubmissionValid = validateConditionalFormFields(data);
    if (!isFormSubmissionValid) {
      setFiltersErrors(true);
      return;
    }
    if (data && isFormSubmissionValid) {
      setSlideIndex(0);
      const services = mapSelectedTypeIdToServiceIds(data.service);

      const constructedBody: ICalendarProps = {
        booking_start_date: data.date,
        clinic_id: data.clinics,
        service_id: services,
        staff_id: data.providersId,
      };
      dispatch(
        setLoading({
          key: "isStaffCalendarLoadingInProgress",
          status: true,
        }),
      );
      fetchBookingDetails(constructedBody);
      dispatch(setCalendarFilter({ data: constructedBody }));
      // i need to show those provider which i select
      const filteredData = providers?.filter((item) => data?.providersId?.includes(item.staff_id));
      dispatch(setCalendarProviders({ data: filteredData }));

      //
    }
  };

  const l1ServiceItems = useSelectMenuReducer(serviceTypeGroups, "name", "id", {
    withAllSelectOption: true,
    allSelectItemOptions: {
      title: "All Services",
    },
  });
  const cityItems = useSelectMenuReducer(cities, "name", "id", {
    withAllSelectOption: false,
    allSelectItemOptions: {
      title: "All Cities",
    },
  });
  const providerItems = useSelectMenuReducer(providers, "name", "staff_id");

  const clinicItems = useSelectMenuReducer<IClinicListResponseData>(clinics, "display_name", "id", {
    withAllSelectOption: false,
    allSelectItemOptions: {
      title: "All Clinics",
    },
  });

  const formHook = useForm<ICalendarFiltersFormFields>({
    mode: "onChange",
    defaultValues: {
      clinics: mappedClinicId,
      providersId: [],
      city: mappedCityId,
      service: "-1",
      date: format(new Date(Date.now()), stringConstants?.fieldDOBFormat),
    },
  });

  useEffect(() => {

    const form_data = formHook.getValues();
    
    const services = mapSelectedTypeIdToServiceIds(form_data.service);

    if (
      form_data.city &&
      form_data.clinics &&
      form_data.providersId.length > 0 &&
      orgServices.length > 0 &&
      !initialFetched
    ) {
      setInitialFetched(true);
      const constructedBody: ICalendarProps = {
        booking_start_date: form_data.date,
        clinic_id: mappedClinicId,
        service_id: services,
        staff_id: form_data.providersId,
      };
      dispatch(setCalendarFilter({ data: constructedBody }));
    }
  }, [
    formHook.watch("city"),
    formHook.watch("clinics"),
    formHook.watch("providersId"),
    formHook.watch("service"),
    orgServices,
    serviceTypeGroups,
  ]);

  useEffect(() => {
    if (calendarFilter && !initialFetched) {
      const constructedBody: ICalendarProps = {
        booking_start_date: calendarFilter.booking_start_date,
        clinic_id: calendarFilter.clinic_id,
        service_id: calendarFilter.service_id,
        staff_id: calendarFilter.staff_id,
      };
      fetchBookingDetails(constructedBody);
      dispatch(setCalendarProviders({ data: providers }));
    }
  }, [calendarFilter]);

  useEffect(() => {
    if (didFetchStaffCalendarServices.current) return;
    didFetchStaffCalendarServices.current = true;
    fetchOrgServicesByFilter({
      filterType: "staff_calendar",
    });
  }, [fetchOrgServicesByFilter]);

  useEffect(() => {
    if (mappedCityId) {
      fetchCities({
        withClinics: true,
        withClinicProviders: false,
        clinicOptions: {
          cityId: mappedCityId,
        },
      });
    }
  }, [mappedCityId]);

  useEffect(() => {
    const formFields: ICalendarFiltersFormFields = formHook.getValues();
    if (formHook.watch("clinics")?.length > 0) {

      let fetchProviderConfig: ICalendarFromProps = {
        ROTADate: formFields?.date,
        serviceId:
          formFields?.service === "-1" ? "" : mapSelectedTypeIdToServiceIds(formFields?.service).join(","),
      };
      fetchProviderConfig = { ...fetchProviderConfig, clinicIds: [formFields?.clinics] };
      fetchProviders(fetchProviderConfig);
      // formHook.setValue("providersId", []);
    }
  }, [formHook.watch("clinics"), formHook.watch("date"), formHook.watch("service")]);

  return (
    <div className="w-full">
      <form className="w-full" onSubmit={formHook.handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 items-end gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 xl:gap-3">
          <PWLCalendarComponent
            // showFutureYears
            allowPastDaySelection={true}
            errorMessages={[{ message: "Date is required", type: "required" }]}
            register={formHook.register("date", {
              required: true,
            })}
            fieldError={formHook.formState.errors.date}
            onSelectDate={(date) => {
              formHook.setValue("date", format(date, stringConstants?.fieldDOBFormat));
            }}
            defaultSelectedDate={formHook.getValues("date")}
            containerClassName=""
            className=""
          />
          <SearchableSelectMenu
            onSelectItem={(item) => {
              formHook.setValue("city", item.id);
              formHook.setValue("providersId", []);
              fetchCities({
                withClinics: true,
                withClinicProviders: true,
                clinicOptions: {
                  cityId: item.id,
                },
              });
            }}
            errorMessages={[{ message: "Required", type: "required" }]}
            fieldError={formHook.formState.errors.city}
            register={formHook.register("city", { required: false })}
            className=""
            showTypedErrors={false}
            showDropdownIcon
            containerClassName=""
            placeholder="Select City"
            selectItems={cityItems}
            defaultSelected={cityItems?.filter((ci) => ci.id === formHook.watch("city"))[0]}
            autoComplete="false"
          />
          <SearchableSelectMenu
            onSelectItem={(item) => {
              formHook.setValue("clinics", item.id);
            }}
            errorMessages={[{ message: "Required", type: "required" }]}
            fieldError={formHook.formState.errors.clinics}
            register={formHook.register("clinics", { required: false })}
            className=""
            showTypedErrors={false}
            showDropdownIcon
            containerClassName=""
            placeholder="Select clinics"
            selectItems={clinicItems}
            defaultSelected={clinicItems?.filter((ci) => ci.id === formHook.watch("clinics"))[0]}
            autoComplete="false"
          />

          <SearchableSelectMenu
            onSelectItem={(item) => {
              formHook.setValue("service", item.id);
              formHook.clearErrors("service");
            }}
            errorMessages={[{ message: "L1 Service Required", type: "required" }]}
            fieldError={formHook.formState.errors.service}
            register={formHook.register("service", {
              required: true,
            })}
            showDropdownIcon
            showTypedErrors={false}
            placeholder="Select Service"
            selectItems={l1ServiceItems}
            defaultSelected={l1ServiceItems?.filter((l1s) => l1s.id === formHook.watch("service"))[0]}
            autoComplete="false"
          />

          <SearchableMultiSelectMenu
            onSelectItem={(items) => {
              const providerIds = items.map((i) => i.id);
              formHook.setValue("providersId", providerIds);
            }}
            withDropDownSearch
            errorMessages={[{ message: "Required", type: "required" }]}
            fieldError={formHook.formState.errors.providersId}
            register={formHook.register("providersId", {
              required: true,
            })}
            withSelectAll
            defaultSelectAll
            showTooltips={true}
            showTypedErrors={false}
            containerClassName=""
            placeholder="Select Providers"
            defaultSelected={providerItems.filter((c) => formHook.watch("providersId").includes(c.id))}
            selectItems={providerItems}
          />
          <button
            type="submit"
            className="h-10 w-full rounded-md bg-black px-3 text-xs font-medium text-white sm:col-span-2 lg:col-span-1 xl:h-auto xl:min-h-[38px]"
          >
            Apply Filters
          </button>
        </div>
      </form>
    </div>
  );
};

export default CalendarViewFilterComponent;
